import * as React from "react";
import { Dispatch } from "redux";
import { connect } from "react-redux";
import { withStyles, createStyles, WithStyles, Theme } from "@material-ui/core";
import { translate, InjectedTranslateProps } from "react-i18next";

import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import LinearProgress from "@material-ui/core/LinearProgress";
import FingerprintIcon from "@material-ui/icons/Fingerprint";

import * as Api from "../api/index";
import { StoreState } from "../reducers/root-reducer";
import { SnackbarVariant } from "../actions/store/type";
import { setSnackbarMessage } from "../actions/store/snackbar-actions";
import { IntegrationCommunicator } from "../integration";
import { Capabilities } from "../integration/capabilities";
import { setLoginStatusAndAuthInfo } from "../actions/auth-actions";

export interface LoginProps {}

interface AuthPayload {
	success: boolean;
	reason?: string;
	error?: object;
	username?: string;
	password?: string;
}

interface State {
	isLoading: boolean;
	username: string;
	password: string;
}

interface ConnectedLoginProps extends WithStyles<typeof styles> {
	capabilities: Capabilities;
	setSnackbarMessage: (message: string, variant?: SnackbarVariant) => void;
}

type Props = LoginProps & ConnectedLoginProps & InjectedTranslateProps;

let firstLogin = true;

export class Login extends React.Component<Props, State> {
	public state: State = {
		isLoading: false,
		username: "",
		password: ""
	};

	public constructor(props: Props) {
		super(props);

		this.handleSubmit = this.handleSubmit.bind(this);
		this.handleUsernameChange = this.handleUsernameChange.bind(this);
		this.handlePasswordChange = this.handlePasswordChange.bind(this);
	}

	public componentDidMount() {
		IntegrationCommunicator.on("credentials", data => {
			try {
				const credentials = JSON.parse(
					(data as any).payload.credentials
				);

				if (credentials.username && credentials.password) {
					this.setState(
						{
							username: credentials.username,
							password: credentials.password
						},
						() => {
							if (firstLogin) {
								this.login();
							}
						}
					);
				}
			} catch (e) {}
		});

		IntegrationCommunicator.request("getCredentials");

		if (navigator.userAgent.indexOf("Tableman/Desktop") > -1) {
			const username = localStorage.getItem("username");
			const password = localStorage.getItem("password");

			if (username && password) {
				this.setState({
					username,
					password
				});
			}
		}
	}

	private handleUsernameChange(e: React.ChangeEvent<HTMLInputElement>) {
		this.setState({ username: e.target.value });
	}

	private handlePasswordChange(e: React.ChangeEvent<HTMLInputElement>) {
		this.setState({ password: e.target.value });
	}

	private handleTouchIDClick = () => {
		if (!this.props.capabilities.touchID) {
			return;
		}

		this.setState({ isLoading: true });

		IntegrationCommunicator.on("auth", data => {
			const result = data.payload as AuthPayload;

			if (result.success) {
				this.login(result.username, result.password);
			} else {
				this.setState({
					isLoading: false
				});

				this.props.setSnackbarMessage(
					"Login mit Fingerabdruck nicht möglich!",
					"error"
				);
			}
		});

		IntegrationCommunicator.request("authenticate");
	};

	private login(username?: string, password?: string) {
		this.setState({ isLoading: true });

		if (!username) {
			username = this.state.username;
		}

		if (!password) {
			password = this.state.password;
		}

		Api.authenticate(username, password).then((success: boolean) => {
			if (success) {
				setLoginStatusAndAuthInfo(true);

				IntegrationCommunicator.send("saveCredentials", {
					value: JSON.stringify({ username, password })
				});

				if (navigator.userAgent.indexOf("Tableman/Desktop") > -1) {
					localStorage.setItem("username", username);
					localStorage.setItem("password", password);
				}

				firstLogin = false;
			} else {
				this.props.setSnackbarMessage("Login fehlgeschlagen!", "error");
				this.setState({ isLoading: false });
			}
		});
	}

	private handleSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();

		this.login();
	}

	public render() {
		const { capabilities, classes, t } = this.props;

		return (
			<Grid
				container
				justify="center"
				alignItems="center"
				className={classes.grid}
			>
				<Grid item>
					<Paper className={classes.paper} elevation={1}>
						{this.state.isLoading && (
							<LinearProgress className={classes.progressbar} />
						)}
						<Typography variant="headline" component="h3">
							{t("login.heading")}
						</Typography>
						<form
							id="login-form"
							autoComplete="off"
							autoCorrect="off"
							autoCapitalize="none"
							onSubmit={this.handleSubmit}
						>
							<Grid container direction="column">
								<TextField
									id="username"
									label={t("login.usernameLabel")}
									margin="normal"
									value={this.state.username}
									onChange={this.handleUsernameChange}
									autoComplete="off"
									autoCorrect="off"
									autoCapitalize="none"
									required
								/>
								<TextField
									id="password"
									label={t("login.passwordLabel")}
									margin="normal"
									type="password"
									value={this.state.password}
									onChange={this.handlePasswordChange}
									required
								/>
								<div className={classes.buttonWrapper}>
									{/*capabilities.touchID && (
										<Button
											className={classes.iconButton}
											onClick={this.handleTouchIDClick}
											variant="contained"
											disabled={this.state.isLoading}
										>
											<FingerprintIcon />
										</Button>
									)*/}
									<Button
										id="submit"
										type="submit"
										variant="contained"
										color="primary"
										disabled={this.state.isLoading}
										fullWidth
									>
										{t("login.submitLabel")}
									</Button>
								</div>
							</Grid>
						</form>
					</Paper>
				</Grid>
			</Grid>
		);
	}
}

const styles = (theme: Theme) =>
	createStyles({
		grid: {
			height: "100vh"
		},
		buttonWrapper: {
			display: "flex",
			position: "relative",
			marginTop: 25
		},
		iconButton: {
			marginRight: 8,
			paddingLeft: 12,
			paddingRight: 12,
			minWidth: 0
		},
		paper: {
			...theme.mixins.gutters(),
			paddingTop: theme.spacing.unit * 2,
			paddingBottom: theme.spacing.unit * 2,
			position: "relative",
			overflow: "hidden"
		},
		progressbar: {
			position: "absolute",
			top: 0,
			left: 0,
			width: "100%"
		}
	});

const mapStateToProps = (state: StoreState) => ({
	capabilities: state.capabilities
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	setSnackbarMessage: (message: string, variant: SnackbarVariant) =>
		dispatch(setSnackbarMessage(message, variant))
});

export const ConnectedLogin = translate("translations")(
	withStyles(styles)(
		connect(
			mapStateToProps,
			mapDispatchToProps
		)(Login)
	)
);
